import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import { useRouter } from 'next/router'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import { SUPPORTED_LOCALES } from 'i18n/locales'
import { Button } from './Button'
import type { Locales, Localization } from '@captaincause/types'
import type { IconKeys } from '@captaincause/ui/Icon'

function getLocaleName(locale: Locales) {
  switch (locale) {
    case 'fr':
      return 'Français'
    case 'en':
      return 'English'
    default:
      return locale
  }
}

interface LocalizationToggleProps {
  localizations: Localization[]
  className?: string
  direction?: 'up' | 'down'
}

export function LocalizationToggle({
  localizations,
  className = '',
  direction = 'down',
}: LocalizationToggleProps) {
  const router = useRouter()
  const locale = useNextLocale()

  const filteredLocalizations =
    localizations?.filter(
      localization =>
        localization.locale !== locale &&
        SUPPORTED_LOCALES.includes(localization.locale),
    ) ?? []

  if (filteredLocalizations.length === 0) return null

  return (
    <div className={tm('relative z-40', className)}>
      <Menu key={locale} as="div" className="text-left">
        <MenuButton
          className="flex h-full w-full items-center justify-center rounded-lg text-sm"
          aria-label="Language menu"
        >
          <Icon
            icon={locale as IconKeys}
            className="mr-1 rounded-sm"
            size="small"
          />
          <Icon icon="chevron" size="small" />
        </MenuButton>
        <MenuItems
          transition
          anchor={{ to: 'bottom end', gap: '4px' }}
          className={tm(
            'z-[99] divide-y rounded-md bg-white shadow-lg transition-transform focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0',
          )}
        >
          <div className="p-1">
            {filteredLocalizations.map(localization => (
              <MenuItem key={localization.locale}>
                <Button
                  variant="naked"
                  onClick={() => {
                    window.location.href = `${
                      localization.locale === 'fr'
                        ? ''
                        : `/${localization.locale}`
                    }${localization.slug ?? router.asPath}`
                  }}
                  className="rounded-md p-2"
                  innerClassName="flex items-center"
                  full
                  locale={false}
                >
                  <Icon
                    icon={localization.locale as IconKeys}
                    className="mr-2 rounded-sm"
                    size="small"
                  />
                  {getLocaleName(localization.locale)}
                </Button>
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Menu>
    </div>
  )
}
